/** Requerimientos movil: lista + alta alineada a la web + detalle con adjuntos. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { tenantIdOrNull, tenantScoped } from '@/config/queryClient';
import {
  commercialService,
  type ListResponse,
  type MobileAttachmentDto,
  type MobileObservationDto,
  type MobileRequirementDto,
} from '@/services/commercialService';
import { sequenceService, type SequencePolicy } from '@/services/sequenceService';
import { tasksService, type WorkflowContext } from '@/services/tasksService';
import { useMobileAuth } from '@/state/MobileAuthContext';

export type MobileRequirement = MobileRequirementDto;

export type RequirementFilters = {
  source?: 'all' | 'real' | 'quote_generator';
  status?: string;
  type?: string;
};

export function useMobileRequirementsQuery(filters: RequirementFilters = {}) {
  const { user, hasPermission } = useMobileAuth();
  const tenantId = tenantIdOrNull(user?.active_tenant_id);
  const source = filters.source ?? 'all';
  const status = filters.status ?? 'all';
  const type = filters.type ?? 'all';
  return useQuery<ListResponse<MobileRequirement>>({
    queryKey:
      tenantId !== null
        ? tenantScoped(tenantId, 'mobile-requirements', source, status, type)
        : ['tenant', 'no-context', 'mobile-requirements', source, status, type],
    queryFn: () =>
      commercialService.requirements({
        page_size: 100,
        ...(source !== 'all' ? { source } : {}),
        ...(status !== 'all' ? { status } : {}),
        ...(type !== 'all' ? { type } : {}),
      }),
    enabled: tenantId !== null && hasPermission('customer_requirements.view'),
    staleTime: 30_000,
  });
}

/** Política de numeración de requerimientos del tenant activo. */
export function useRequirementSequencePolicyQuery(enabled = true) {
  const { user } = useMobileAuth();
  const tenantId = tenantIdOrNull(user?.active_tenant_id);
  return useQuery<SequencePolicy>({
    queryKey:
      tenantId !== null
        ? tenantScoped(tenantId, 'requirement-sequence-policy')
        : ['tenant', 'no-context', 'requirement-sequence-policy'],
    queryFn: () => sequenceService.policy('customer_requirements'),
    enabled: tenantId !== null && enabled,
    staleTime: 30_000,
  });
}

export function useMobileRequirementQuery(requirementId: string | null) {
  const { user, hasPermission } = useMobileAuth();
  const tenantId = tenantIdOrNull(user?.active_tenant_id);
  return useQuery<MobileRequirement>({
    queryKey:
      tenantId !== null
        ? tenantScoped(tenantId, 'mobile-requirement', requirementId)
        : ['tenant', 'no-context', 'mobile-requirement', requirementId],
    queryFn: () => commercialService.getRequirement(requirementId!),
    enabled:
      tenantId !== null &&
      requirementId !== null &&
      requirementId !== '' &&
      hasPermission('customer_requirements.view'),
    staleTime: 30_000,
  });
}

export function useMobileRequirementAttachmentsQuery(requirementId: string | null) {
  const { user, hasPermission } = useMobileAuth();
  const tenantId = tenantIdOrNull(user?.active_tenant_id);
  return useQuery<MobileAttachmentDto[]>({
    queryKey:
      tenantId !== null
        ? tenantScoped(tenantId, 'mobile-requirement-attachments', requirementId)
        : ['tenant', 'no-context', 'mobile-requirement-attachments', requirementId],
    queryFn: () => commercialService.requirementAttachments(requirementId!),
    enabled:
      tenantId !== null &&
      requirementId !== null &&
      requirementId !== '' &&
      hasPermission('customer_requirements.view'),
    staleTime: 30_000,
  });
}

export function useCreateRequirementMutation() {
  const queryClient = useQueryClient();
  const { user } = useMobileAuth();
  const tenantId = tenantIdOrNull(user?.active_tenant_id);
  return useMutation({
    mutationFn: async (input: { body: Record<string, unknown>; files: File[] }) => {
      const created = (await commercialService.createRequirement(input.body)) as { id: string };
      if (input.files.length > 0) {
        await commercialService.uploadAttachments('customer-requirements', created.id, input.files);
      }
      return created;
    },
    onSuccess: () => {
      if (tenantId !== null) {
        void queryClient.invalidateQueries({
          queryKey: tenantScoped(tenantId, 'mobile-requirements'),
        });
      }
    },
  });
}

export function useUploadRequirementAttachmentsMutation() {
  const queryClient = useQueryClient();
  const { user } = useMobileAuth();
  const tenantId = tenantIdOrNull(user?.active_tenant_id);
  return useMutation({
    mutationFn: (input: { requirementId: string; files: File[] }) =>
      commercialService.uploadAttachments('customer-requirements', input.requirementId, input.files),
    onSuccess: (_data, input) => {
      if (tenantId !== null) {
        void queryClient.invalidateQueries({
          queryKey: tenantScoped(tenantId, 'mobile-requirement-attachments', input.requirementId),
        });
        void queryClient.invalidateQueries({
          queryKey: tenantScoped(tenantId, 'mobile-requirement', input.requirementId),
        });
      }
    },
  });
}

/** Contexto de flujo del requerimiento: acciones + recorrido + revisión. */
export function useMobileRequirementContextQuery(requirementId: string | null) {
  const { user } = useMobileAuth();
  const tenantId = tenantIdOrNull(user?.active_tenant_id);
  return useQuery<WorkflowContext>({
    queryKey:
      tenantId !== null
        ? tenantScoped(tenantId, 'mobile-requirement-context', requirementId)
        : ['tenant', 'no-context', 'mobile-requirement-context', requirementId],
    queryFn: () => tasksService.context('customer_requirement', requirementId!),
    enabled: tenantId !== null && requirementId !== null && requirementId !== '',
    staleTime: 30_000,
  });
}

function invalidateRequirement(queryClient: ReturnType<typeof useQueryClient>, tenantId: string, requirementId: string) {
  void queryClient.invalidateQueries({
    queryKey: tenantScoped(tenantId, 'mobile-requirement', requirementId),
  });
  void queryClient.invalidateQueries({
    queryKey: tenantScoped(tenantId, 'mobile-requirement-context', requirementId),
  });
  void queryClient.invalidateQueries({
    queryKey: tenantScoped(tenantId, 'mobile-requirements'),
  });
}

/** Ejecuta una acción de flujo del requerimiento con revisión del documento. */
export function useRequirementActionMutation(requirementId: string) {
  const queryClient = useQueryClient();
  const { user } = useMobileAuth();
  const tenantId = tenantIdOrNull(user?.active_tenant_id);
  return useMutation({
    mutationFn: (input: { action: string; body?: Record<string, unknown>; revision?: number }) =>
      commercialService.requirementAction(requirementId, input.action, input.body ?? {}, input.revision),
    onSuccess: () => {
      if (tenantId !== null) invalidateRequirement(queryClient, tenantId, requirementId);
    },
  });
}

/** Actualiza nombre, fecha requerida y descripción (PUT, vuelve al detalle). */
export function useUpdateRequirementMutation(requirementId: string) {
  const queryClient = useQueryClient();
  const { user } = useMobileAuth();
  const tenantId = tenantIdOrNull(user?.active_tenant_id);
  return useMutation({
    mutationFn: (input: { body: Record<string, unknown>; revision?: number }) =>
      commercialService.updateRequirement(requirementId, input.body, input.revision),
    onSuccess: () => {
      if (tenantId !== null) invalidateRequirement(queryClient, tenantId, requirementId);
    },
  });
}

/** Observaciones del requerimiento (CRUD; resolver queda en la web). */
export function useMobileRequirementObservationsQuery(requirementId: string | null) {
  const { user, hasPermission } = useMobileAuth();
  const tenantId = tenantIdOrNull(user?.active_tenant_id);
  return useQuery<MobileObservationDto[]>({
    queryKey:
      tenantId !== null
        ? tenantScoped(tenantId, 'mobile-requirement-observations', requirementId)
        : ['tenant', 'no-context', 'mobile-requirement-observations', requirementId],
    queryFn: () => commercialService.listObservations(requirementId!),
    enabled:
      tenantId !== null &&
      requirementId !== null &&
      requirementId !== '' &&
      hasPermission('customer_requirements.view'),
    staleTime: 30_000,
  });
}

export function useCreateObservationMutation(requirementId: string) {
  const queryClient = useQueryClient();
  const { user } = useMobileAuth();
  const tenantId = tenantIdOrNull(user?.active_tenant_id);
  return useMutation({
    mutationFn: (comment: string) => commercialService.createObservation(requirementId, comment),
    onSuccess: () => {
      if (tenantId !== null) {
        void queryClient.invalidateQueries({
          queryKey: tenantScoped(tenantId, 'mobile-requirement-observations', requirementId),
        });
        invalidateRequirement(queryClient, tenantId, requirementId);
      }
    },
  });
}

export function useUpdateObservationMutation(requirementId: string) {
  const queryClient = useQueryClient();
  const { user } = useMobileAuth();
  const tenantId = tenantIdOrNull(user?.active_tenant_id);
  return useMutation({
    mutationFn: (input: { observationId: string; comment: string }) =>
      commercialService.updateObservation(requirementId, input.observationId, input.comment),
    onSuccess: () => {
      if (tenantId !== null) {
        void queryClient.invalidateQueries({
          queryKey: tenantScoped(tenantId, 'mobile-requirement-observations', requirementId),
        });
        invalidateRequirement(queryClient, tenantId, requirementId);
      }
    },
  });
}

export function useDeleteObservationMutation(requirementId: string) {
  const queryClient = useQueryClient();
  const { user } = useMobileAuth();
  const tenantId = tenantIdOrNull(user?.active_tenant_id);
  return useMutation({
    mutationFn: (observationId: string) =>
      commercialService.deleteObservation(requirementId, observationId),
    onSuccess: () => {
      if (tenantId !== null) {
        void queryClient.invalidateQueries({
          queryKey: tenantScoped(tenantId, 'mobile-requirement-observations', requirementId),
        });
        invalidateRequirement(queryClient, tenantId, requirementId);
      }
    },
  });
}
